// Клавиши пульта (задача D4): одна таблица `HOTKEYS` на весь пульт. Из неё берут обработчики экранов
// (`useHotkeys`), шпаргалка «?» (D5) и `docs/guide-console.md` (D21).
//
// Клавиши ловим по `event.code` — месту клавиши, а не букве: J в русской раскладке — «о», а работать должна так же.
// Модификаторы — точным набором: Shift+7 («?» в русской раскладке) не равно 7 («7 дней» в «Обзоре»). В полях ввода,
// редакторах и открытых меню клавиши экрана не перехватываем: там работает одна Ctrl K.
import type { Role } from '@contract/index.js';
import { ROLES } from '@contract/index.js';
import { useLayoutEffect, useRef } from 'react';
import { useRole } from '@/api/queries/auth';
import { isSessionExpired } from '@/auth/session';

export type HotkeyScope = 'global' | 'readiness' | 'overview' | 'dialogs' | 'dialog' | 'review' | 'tests';

export type Hotkey = {
  /** Действие. У одного действия может быть несколько клавиш (J и ↓) — у строк один id. */
  id: string;
  /** `KeyboardEvent.code`: `KeyJ`, `Digit1`, `ArrowDown`, `Slash`, `Enter`. */
  code: string;
  shift?: true;
  /** Ctrl или ⌘. */
  mod?: true;
  scope: HotkeyScope;
  roles: Role[];
  /** Как клавиша написана для людей: «J», «Ctrl K», «?». */
  keysRu: string;
  labelRu: string;
};

const ALL: Role[] = [...ROLES];
const ADMIN: Role[] = ['admin'];
const VIEWERS: Role[] = ['viewer', 'editor'];

export const HOTKEYS: readonly Hotkey[] = [
  // Везде
  { id: 'command', code: 'KeyK', mod: true, scope: 'global', roles: ALL, keysRu: 'Ctrl K', labelRu: 'Поиск и команды' },
  { id: 'shortcuts', code: 'Slash', shift: true, scope: 'global', roles: ALL, keysRu: '?', labelRu: 'Шпаргалка клавиш' },

  // Готовность: R у админа — «Проверить всё» (новый прогон), у остальных — «Обновить» (перечитать снимок).
  { id: 'readiness.check-all', code: 'KeyR', scope: 'readiness', roles: ADMIN, keysRu: 'R', labelRu: 'Проверить всё' },
  { id: 'readiness.refresh', code: 'KeyR', scope: 'readiness', roles: VIEWERS, keysRu: 'R', labelRu: 'Обновить' },
  { id: 'readiness.why', code: 'Slash', scope: 'readiness', roles: ALL, keysRu: '/', labelRu: 'Почему не ответила?' },

  // Обзор
  { id: 'overview.today', code: 'Digit1', scope: 'overview', roles: ALL, keysRu: '1', labelRu: 'Сегодня' },
  { id: 'overview.week', code: 'Digit7', scope: 'overview', roles: ALL, keysRu: '7', labelRu: '7 дней' },
  { id: 'overview.month', code: 'Digit3', scope: 'overview', roles: ALL, keysRu: '3', labelRu: '30 дней' },
  { id: 'overview.refresh', code: 'KeyR', scope: 'overview', roles: ALL, keysRu: 'R', labelRu: 'Обновить' },

  // Диалоги (список)
  { id: 'dialogs.next', code: 'KeyJ', scope: 'dialogs', roles: ALL, keysRu: 'J', labelRu: 'Следующий диалог в списке' },
  { id: 'dialogs.next', code: 'ArrowDown', scope: 'dialogs', roles: ALL, keysRu: '↓', labelRu: 'Следующий диалог в списке' },
  { id: 'dialogs.prev', code: 'KeyK', scope: 'dialogs', roles: ALL, keysRu: 'K', labelRu: 'Предыдущий диалог в списке' },
  { id: 'dialogs.prev', code: 'ArrowUp', scope: 'dialogs', roles: ALL, keysRu: '↑', labelRu: 'Предыдущий диалог в списке' },
  { id: 'dialogs.open', code: 'Enter', scope: 'dialogs', roles: ALL, keysRu: 'Enter', labelRu: 'Открыть диалог' },
  { id: 'dialogs.search', code: 'Slash', scope: 'dialogs', roles: ALL, keysRu: '/', labelRu: 'Поиск' },

  // Разбор диалога
  { id: 'dialog.next', code: 'KeyJ', scope: 'dialog', roles: ALL, keysRu: 'J', labelRu: 'Следующий диалог' },
  { id: 'dialog.prev', code: 'KeyK', scope: 'dialog', roles: ALL, keysRu: 'K', labelRu: 'Предыдущий диалог' },
  { id: 'dialog.next-message', code: 'ArrowDown', scope: 'dialog', roles: ALL, keysRu: '↓', labelRu: 'Следующее сообщение' },
  { id: 'dialog.prev-message', code: 'ArrowUp', scope: 'dialog', roles: ALL, keysRu: '↑', labelRu: 'Предыдущее сообщение' },
  { id: 'dialog.good', code: 'Digit1', scope: 'dialog', roles: ADMIN, keysRu: '1', labelRu: 'Хорошо' },
  { id: 'dialog.bad', code: 'Digit2', scope: 'dialog', roles: ADMIN, keysRu: '2', labelRu: 'Плохо' },
  { id: 'dialog.fix', code: 'KeyE', scope: 'dialog', roles: ADMIN, keysRu: 'E', labelRu: 'Исправить → в примеры' },
  { id: 'dialog.article', code: 'KeyN', scope: 'dialog', roles: ADMIN, keysRu: 'N', labelRu: 'Статья базы' },
  { id: 'dialog.copy-trace', code: 'KeyT', scope: 'dialog', roles: ADMIN, keysRu: 'T', labelRu: 'Скопировать ID трассы' },

  // На проверку
  { id: 'review.next', code: 'KeyJ', scope: 'review', roles: ADMIN, keysRu: 'J', labelRu: 'Следующий' },
  { id: 'review.prev', code: 'KeyK', scope: 'review', roles: ADMIN, keysRu: 'K', labelRu: 'Предыдущий' },
  { id: 'review.accept', code: 'KeyA', scope: 'review', roles: ADMIN, keysRu: 'A', labelRu: 'Принять' },
  { id: 'review.fix', code: 'KeyE', scope: 'review', roles: ADMIN, keysRu: 'E', labelRu: 'Исправить' },
  { id: 'review.skip', code: 'KeyS', scope: 'review', roles: ADMIN, keysRu: 'S', labelRu: 'Пропустить' },
  { id: 'review.undo', code: 'KeyZ', mod: true, scope: 'review', roles: ADMIN, keysRu: 'Ctrl Z', labelRu: 'Отменить последнее действие' },

  // Тесты
  { id: 'tests.next', code: 'KeyJ', scope: 'tests', roles: ALL, keysRu: 'J', labelRu: 'Следующая строка' },
  { id: 'tests.prev', code: 'KeyK', scope: 'tests', roles: ALL, keysRu: 'K', labelRu: 'Предыдущая строка' },
  { id: 'tests.open', code: 'Enter', scope: 'tests', roles: ALL, keysRu: 'Enter', labelRu: 'Открыть прогон' },
];

/** Действие, которое работает и в полях ввода. */
const WORKS_WHILE_TYPING = 'command';

/** Клавиша-символ («?», «/»): подпись строки — один знак, не буква и не цифра. */
const SYMBOL_KEY = /^[^\p{L}\p{N}\s]$/u;

/**
 * Событие подходит строке таблицы, без Alt (AltGr на Windows — это Ctrl+Alt), не набор иероглифов и не обработанное
 * раньше событие, и:
 * - та же клавиша по месту (`code`), Shift и Ctrl/⌘ ровно как в строке — так буквы работают в любой раскладке;
 * - или, у символьных клавиш («?», «/», D5–D7 по UX-ревью), — тот же символ (`key`) при любом Shift: в русской раскладке
 *   «?» — это Shift+7, а «/» — Shift+\, и по месту клавиши их не найти. Цифры символами не считаются: Shift+7 не равно
 *   «7» (Review Focus 5).
 */
export function matchesHotkey(e: KeyboardEvent, h: Hotkey): boolean {
  if (e.altKey || e.isComposing || e.defaultPrevented) return false;
  const mod = (e.ctrlKey || e.metaKey) === (h.mod === true);
  if (!mod) return false;
  if (e.code === h.code && e.shiftKey === (h.shift === true)) return true;
  return SYMBOL_KEY.test(h.keysRu) && e.key === h.keysRu;
}

/** Поля `input`, в которых не печатают: клавиши экрана в них работают. */
const BUTTON_LIKE_INPUTS = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'image']);

/**
 * Клавиша идёт в поле, редактор или открытое меню: поля ввода (кроме флажков, переключателей и кнопок), textarea,
 * select, редактируемый текст, роли textbox и combobox, всё внутри меню (`menu`), списка выбора (`listbox`)
 * и области `[data-hotkeys="off"]`.
 */
export function isTypingTarget(t: EventTarget | null): boolean {
  if (!(t instanceof Element)) return false;
  if (t instanceof HTMLInputElement) return !BUTTON_LIKE_INPUTS.has(t.type);
  if (t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement) return true;
  const editable = t.closest('[contenteditable]');
  if (editable !== null && editable.getAttribute('contenteditable') !== 'false') return true;
  if (t.matches('[role="textbox"], [role="combobox"]')) return true;
  return t.closest('[role="menu"], [role="listbox"], [data-hotkeys="off"]') !== null;
}

/** Элементы, которые сами нажимаются по Enter: кнопка, ссылка, вкладка, флажок. */
const ENTER_OWNERS =
  'button, a[href], summary, [role="button"], [role="link"], [role="tab"], [role="checkbox"], [role="switch"], [role="radio"]';

/** Виджеты, которые сами ходят стрелками: вкладки и сегменты, переключатели, ползунок, дерево, панель кнопок. */
const ARROW_OWNERS =
  '[role="tablist"], [role="radiogroup"], [role="slider"], [role="spinbutton"], [role="tree"], [role="toolbar"]';

/**
 * Клавишу забирает сам элемент в фокусе: Enter на кнопке нажимает её, стрелки в сегментах и переключателях
 * двигают выбор. Иначе «Открыть диалог» по Enter перебил бы кнопку, на которой стоит фокус.
 */
export function widgetOwnsKey(t: EventTarget | null, code: string): boolean {
  if (!(t instanceof Element)) return false;
  if (code === 'Enter') return t.closest(ENTER_OWNERS) !== null;
  if (code === 'ArrowUp' || code === 'ArrowDown') return t.closest(ARROW_OWNERS) !== null;
  return false;
}

/**
 * Клавиши экрана: только строки своего экрана и «Везде», только доступные роли вошедшего. `handlers` — по id строки;
 * сработал обработчик — действие браузера по умолчанию отменено. Пока открыто окно «Сессия истекла», клавиши молчат.
 */
export function useHotkeys(scope: HotkeyScope, handlers: Partial<Record<string, (e: KeyboardEvent) => void>>): void {
  const role = useRole();
  const handlersRef = useRef(handlers);
  // Слушатель ставится в useLayoutEffect: клавиши работают с первого кадра экрана, а не после отрисовки.
  useLayoutEffect(() => {
    handlersRef.current = handlers;
  });

  useLayoutEffect(() => {
    if (role === undefined) return undefined;
    const active = HOTKEYS.filter((h) => (h.scope === scope || h.scope === 'global') && h.roles.includes(role));

    function onKeyDown(e: KeyboardEvent): void {
      if (isSessionExpired() || widgetOwnsKey(e.target, e.code)) return;
      const typing = isTypingTarget(e.target);
      for (const h of active) {
        if (typing && h.id !== WORKS_WHILE_TYPING) continue;
        if (!matchesHotkey(e, h)) continue;
        const handler = handlersRef.current[h.id];
        if (handler === undefined) continue;
        e.preventDefault();
        handler(e);
        return;
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [scope, role]);
}
